import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Check, Crown, User, Phone, Image, 
  Briefcase, Calendar, CreditCard, Settings, Eye, Copy,
  Plus, X, Trash2, Loader2, Sparkles, Star, Clock,
  Palette, MapPin, Mail, Instagram, MessageSquare
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { colorPalettes, portfolioTemplates, getTemplateCoverImages, getDefaultPalette } from '../data/portfolioMock';
import MasterAccess from '../components/MasterAccess';

const STEPS = [
  { id: 1, title: 'Dados', icon: User },
  { id: 2, title: 'Contato', icon: Phone },
  { id: 3, title: 'Portfólio', icon: Image },
  { id: 4, title: 'Serviços', icon: Briefcase },
  { id: 5, title: 'Agenda', icon: Calendar },
  { id: 6, title: 'Pagamento', icon: CreditCard },
  { id: 7, title: 'Extras', icon: Settings },
  { id: 8, title: 'Ativar', icon: Check },
];

const ProConfigurar = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    // Step 1: Dados básicos
    name: '',
    business: '',
    tagline: '',
    bio: '',
    city: '',
    yearsExperience: '',
    photo: null,
    
    // Step 2: Contato
    phone: '',
    email: '',
    instagram: '',
    
    // Step 3: Portfólio
    template: null,
    colorPalette: null,
    coverPhoto: null,
    categories: [],
    works: [],
    
    // Step 4: Serviços
    services: [],
    
    // Step 5: Agenda
    workingDays: {
      dom: false, seg: true, ter: true, qua: true, qui: true, sex: true, sab: false
    },
    workingHours: { start: '08:00', end: '18:00' },
    slotDuration: 60,
    
    // Step 6: Pagamento
    acceptPix: true,
    acceptCard: true,
    acceptBoleto: false,
    requireDeposit: false,
    depositPercent: 50,
    
    // Step 7: Extras
    zapiInstance: '',
    zapiToken: '',
    reminderMode: 'semi', // semi or auto
    syncGoogleCalendar: false,
    darkMode: false,
    
    // Pro settings
    isPro: true
  });

  const [newService, setNewService] = useState({ name: '', price: '', duration: 60 });
  const [newCategory, setNewCategory] = useState('');

  const slug = formData.business 
    ? formData.business.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
    : 'seu-negocio';
  
  const fullUrl = `${window.location.origin}/pro/${slug}`;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 2 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, photo: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const handleTemplateSelect = (template) => {
    setFormData({
      ...formData,
      template: template.id,
      categories: template.id === 'personalizado' ? formData.categories : [...template.categories],
      colorPalette: getDefaultPalette(template.id),
      coverPhoto: getTemplateCoverImages(template.id)[0]
    });
  };

  const handleAddService = () => {
    if (newService.name && newService.price) {
      setFormData({
        ...formData,
        services: [...formData.services, { ...newService, id: Date.now() }]
      });
      setNewService({ name: '', price: '', duration: 60 });
    }
  };

  const handleRemoveService = (id) => {
    setFormData({
      ...formData,
      services: formData.services.filter(s => s.id !== id)
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Salvar profissional Pro
      const response = await fetch(`${API_URL}/api/pro/professionals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, slug })
      });
      
      if (response.ok) {
        setStep(8);
      } else {
        // Fallback localStorage
        localStorage.setItem(`pro_${slug}`, JSON.stringify({ ...formData, slug }));
        setStep(8);
      }
    } catch (e) {
      localStorage.setItem(`pro_${slug}`, JSON.stringify({ ...formData, slug }));
      setStep(8);
    }
    setLoading(false);
  };

  // Step 1: Dados básicos
  const renderStep1 = () => (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Seus dados</h2>
        <p className="text-slate-400">Informações básicas do seu negócio</p>
      </div>

      {/* Foto */}
      <div className="flex justify-center">
        <button
          onClick={() => photoInputRef.current?.click()}
          className="w-28 h-28 rounded-full border-2 border-dashed border-slate-600 hover:border-amber-500 flex flex-col items-center justify-center transition-all overflow-hidden"
        >
          {formData.photo ? (
            <img src={formData.photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <>
              <User size={32} className="text-slate-500" />
              <span className="text-xs text-slate-500 mt-1">Sua foto</span>
            </>
          )}
        </button>
        <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Seu nome *</Label>
          <Input
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="João Silva"
            className="mt-1 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Nome do negócio *</Label>
          <Input
            name="business"
            value={formData.business}
            onChange={handleInputChange}
            placeholder="João Reformas"
            className="mt-1 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Cidade</Label>
          <Input
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            placeholder="São Paulo, SP"
            className="mt-1 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Anos de experiência</Label>
          <Input
            name="yearsExperience"
            type="number"
            value={formData.yearsExperience}
            onChange={handleInputChange}
            placeholder="10"
            className="mt-1 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div>
        <Label className="text-slate-300">Frase de destaque</Label>
        <Input
          name="tagline"
          value={formData.tagline}
          onChange={handleInputChange}
          placeholder="Transformando sonhos em realidade"
          className="mt-1 bg-slate-800 border-slate-700 text-white"
        />
      </div>

      <div>
        <Label className="text-slate-300">Sobre você</Label>
        <textarea
          name="bio"
          value={formData.bio}
          onChange={handleInputChange}
          placeholder="Conte um pouco sobre sua experiência..."
          rows={3}
          className="w-full mt-1 p-3 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
        />
      </div>
    </div>
  );

  // Step 2: Contato
  const renderStep2 = () => (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Contato</h2>
        <p className="text-slate-400">Como os clientes vão te encontrar</p>
      </div>

      <div>
        <Label className="text-slate-300">WhatsApp *</Label>
        <Input
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="(11) 99999-9999"
          className="mt-1 bg-slate-800 border-slate-700 text-white"
        />
      </div>

      <div>
        <Label className="text-slate-300">E-mail</Label>
        <Input
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="seu@email.com"
          className="mt-1 bg-slate-800 border-slate-700 text-white"
        />
      </div>

      <div>
        <Label className="text-slate-300">Instagram</Label>
        <Input
          name="instagram"
          value={formData.instagram}
          onChange={handleInputChange}
          placeholder="@seuperfil"
          className="mt-1 bg-slate-800 border-slate-700 text-white"
        />
      </div>

      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <p className="text-sm text-slate-400">
          <span className="text-amber-400">💡 Dica Pro:</span> Seu link será{' '}
          <span className="text-white font-mono">ativaja.com/{slug}</span>
        </p>
      </div>
    </div>
  );

  // Step 3: Portfólio
  const renderStep3 = () => (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Portfólio</h2>
        <p className="text-slate-400">Escolha seu estilo visual</p>
      </div>

      {/* Templates */}
      <div>
        <Label className="text-slate-300 mb-3 block">Área de atuação</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {portfolioTemplates.map((template) => {
            const Icon = LucideIcons[template.icon] || LucideIcons.Briefcase;
            const isSelected = formData.template === template.id;
            return (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                }`}
              >
                <Icon size={24} className={isSelected ? 'text-amber-400' : 'text-slate-400'} />
                <h3 className="font-medium text-white mt-2 text-sm">{template.name}</h3>
              </button>
            );
          })}
        </div>
      </div>

      {formData.template && (
        <>
          {/* Cores */}
          <div>
            <Label className="text-slate-300 mb-3 block">Paleta de cores</Label>
            <div className="flex flex-wrap gap-2">
              {colorPalettes.map((palette) => (
                <button
                  key={palette.id}
                  onClick={() => setFormData({ ...formData, colorPalette: palette.id })}
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${palette.gradient} border-2 transition-all ${
                    formData.colorPalette === palette.id
                      ? 'border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  title={palette.name}
                />
              ))}
            </div>
          </div>

          {/* Capa */}
          <div>
            <Label className="text-slate-300 mb-3 block">Imagem de capa</Label>
            <div className="grid grid-cols-3 gap-3">
              {getTemplateCoverImages(formData.template).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setFormData({ ...formData, coverPhoto: img })}
                  className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    formData.coverPhoto === img
                      ? 'border-amber-500'
                      : 'border-transparent hover:border-slate-600'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Step 4: Serviços
  const renderStep4 = () => (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Serviços</h2>
        <p className="text-slate-400">O que você oferece</p>
      </div>

      {/* Lista de serviços */}
      {formData.services.length > 0 && (
        <div className="space-y-2">
          {formData.services.map((service) => (
            <div key={service.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
              <div>
                <p className="text-white font-medium">{service.name}</p>
                <p className="text-sm text-slate-400">{service.duration} min</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-amber-400 font-medium">R$ {service.price}</span>
                <button onClick={() => handleRemoveService(service.id)} className="text-slate-500 hover:text-red-400">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Adicionar serviço */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-400 text-sm">Nome do serviço</Label>
            <Input
              value={newService.name}
              onChange={(e) => setNewService({ ...newService, name: e.target.value })}
              placeholder="Corte de cabelo"
              className="mt-1 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-400 text-sm">Preço (R$)</Label>
            <Input
              type="number"
              value={newService.price}
              onChange={(e) => setNewService({ ...newService, price: e.target.value })}
              placeholder="50"
              className="mt-1 bg-slate-700 border-slate-600 text-white"
            />
          </div>
        </div>
        <div>
          <Label className="text-slate-400 text-sm">Duração (minutos)</Label>
          <select
            value={newService.duration}
            onChange={(e) => setNewService({ ...newService, duration: parseInt(e.target.value) })}
            className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
          >
            <option value={30}>30 min</option>
            <option value={60}>1 hora</option>
            <option value={90}>1h30</option>
            <option value={120}>2 horas</option>
            <option value={180}>3 horas</option>
            <option value={240}>4 horas</option>
          </select>
        </div>
        <Button onClick={handleAddService} className="w-full bg-amber-500 hover:bg-amber-600">
          <Plus size={18} />
          Adicionar serviço
        </Button>
      </div>
    </div>
  );

  // Step 5: Agenda
  const renderStep5 = () => (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Agenda</h2>
        <p className="text-slate-400">Quando você está disponível</p>
      </div>

      {/* Dias */}
      <div>
        <Label className="text-slate-300 mb-3 block">Dias de trabalho</Label>
        <div className="flex gap-2">
          {['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'].map((day) => (
            <button
              key={day}
              onClick={() => setFormData({
                ...formData,
                workingDays: { ...formData.workingDays, [day]: !formData.workingDays[day] }
              })}
              className={`w-12 h-12 rounded-lg font-medium transition-all ${
                formData.workingDays[day]
                  ? 'bg-amber-500 text-white'
                  : 'bg-slate-800 text-slate-400 border border-slate-700'
              }`}
            >
              {day.charAt(0).toUpperCase() + day.slice(1, 3)}
            </button>
          ))}
        </div>
      </div>

      {/* Horários */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-slate-300">Início</Label>
          <Input
            type="time"
            value={formData.workingHours.start}
            onChange={(e) => setFormData({
              ...formData,
              workingHours: { ...formData.workingHours, start: e.target.value }
            })}
            className="mt-1 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-slate-300">Término</Label>
          <Input
            type="time"
            value={formData.workingHours.end}
            onChange={(e) => setFormData({
              ...formData,
              workingHours: { ...formData.workingHours, end: e.target.value }
            })}
            className="mt-1 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      {/* Intervalo entre agendamentos */}
      <div>
        <Label className="text-slate-300">Intervalo entre agendamentos</Label>
        <select
          value={formData.slotDuration}
          onChange={(e) => setFormData({ ...formData, slotDuration: parseInt(e.target.value) })}
          className="w-full mt-1 p-3 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value={30}>30 minutos</option>
          <option value={60}>1 hora</option>
          <option value={90}>1h30</option>
          <option value={120}>2 horas</option>
        </select>
      </div>
    </div>
  );

  // Step 6: Pagamento
  const renderStep6 = () => (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Pagamento</h2>
        <p className="text-slate-400">Como você quer receber</p>
      </div>

      {/* Formas de pagamento */}
      <div className="space-y-3">
        {[
          { key: 'acceptPix', label: 'Pix', desc: 'Receba na hora' },
          { key: 'acceptCard', label: 'Cartão de crédito', desc: 'Até 12x' },
          { key: 'acceptBoleto', label: 'Boleto bancário', desc: '3 dias para compensar' },
        ].map((method) => (
          <button
            key={method.key}
            onClick={() => setFormData({ ...formData, [method.key]: !formData[method.key] })}
            className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
              formData[method.key]
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-slate-700 bg-slate-800/50'
            }`}
          >
            <div className="text-left">
              <p className="text-white font-medium">{method.label}</p>
              <p className="text-sm text-slate-400">{method.desc}</p>
            </div>
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              formData[method.key] ? 'border-amber-500 bg-amber-500' : 'border-slate-600'
            }`}>
              {formData[method.key] && <Check size={14} className="text-white" />}
            </div>
          </button>
        ))}
      </div>

      {/* Sinal */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white font-medium">Exigir sinal?</p>
            <p className="text-sm text-slate-400">Cliente paga parte ao agendar</p>
          </div>
          <button
            onClick={() => setFormData({ ...formData, requireDeposit: !formData.requireDeposit })}
            className={`w-12 h-6 rounded-full transition-all ${
              formData.requireDeposit ? 'bg-amber-500' : 'bg-slate-700'
            }`}
          >
            <div className={`w-5 h-5 bg-white rounded-full transition-all ${
              formData.requireDeposit ? 'ml-6' : 'ml-0.5'
            }`} />
          </button>
        </div>
        
        {formData.requireDeposit && (
          <div>
            <Label className="text-slate-400 text-sm">Porcentagem do sinal</Label>
            <select
              value={formData.depositPercent}
              onChange={(e) => setFormData({ ...formData, depositPercent: parseInt(e.target.value) })}
              className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
            >
              <option value={20}>20%</option>
              <option value={30}>30%</option>
              <option value={50}>50%</option>
              <option value={100}>100% (pagamento total)</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );

  // Step 7: Extras
  const renderStep7 = () => (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Configurações extras</h2>
        <p className="text-slate-400">Personalize sua experiência Pro</p>
      </div>

      {/* Z-API */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center gap-3 mb-4">
          <MessageSquare className="text-green-400" size={24} />
          <div>
            <p className="text-white font-medium">Lembretes WhatsApp</p>
            <p className="text-sm text-slate-400">Envie lembretes automáticos</p>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={() => setFormData({ ...formData, reminderMode: 'semi' })}
            className={`w-full p-3 rounded-lg border text-left ${
              formData.reminderMode === 'semi'
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-slate-700 bg-slate-800'
            }`}
          >
            <p className="text-white font-medium">Semi-automático (grátis)</p>
            <p className="text-xs text-slate-400">Abre WhatsApp com mensagem pronta</p>
          </button>
          
          <button
            onClick={() => setFormData({ ...formData, reminderMode: 'auto' })}
            className={`w-full p-3 rounded-lg border text-left ${
              formData.reminderMode === 'auto'
                ? 'border-amber-500 bg-amber-500/10'
                : 'border-slate-700 bg-slate-800'
            }`}
          >
            <p className="text-white font-medium">Automático (Z-API)</p>
            <p className="text-xs text-slate-400">Sistema envia sozinho</p>
          </button>
        </div>

        {formData.reminderMode === 'auto' && (
          <div className="mt-4 space-y-3">
            <Input
              placeholder="Instance ID do Z-API"
              value={formData.zapiInstance}
              onChange={(e) => setFormData({ ...formData, zapiInstance: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
            />
            <Input
              placeholder="Token do Z-API"
              value={formData.zapiToken}
              onChange={(e) => setFormData({ ...formData, zapiToken: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white"
            />
          </div>
        )}
      </div>

      {/* Tema */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="text-purple-400" size={24} />
            <div>
              <p className="text-white font-medium">Tema escuro</p>
              <p className="text-sm text-slate-400">Página pública em dark mode</p>
            </div>
          </div>
          <button
            onClick={() => setFormData({ ...formData, darkMode: !formData.darkMode })}
            className={`w-12 h-6 rounded-full transition-all ${
              formData.darkMode ? 'bg-amber-500' : 'bg-slate-700'
            }`}
          >
            <div className={`w-5 h-5 bg-white rounded-full transition-all ${
              formData.darkMode ? 'ml-6' : 'ml-0.5'
            }`} />
          </button>
        </div>
      </div>
    </div>
  );

  // Step 8: Sucesso
  const renderStep8 = () => (
    <div className="max-w-xl mx-auto text-center">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-6">
        <Crown size={40} className="text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-white mb-2">Seu Pro está ativo! 🎉</h2>
      <p className="text-slate-400 mb-8">
        Tudo configurado. Compartilhe seu link e comece a receber clientes.
      </p>
      
      <div className="bg-slate-800 rounded-xl p-4 mb-6">
        <p className="text-sm text-slate-400 mb-2">Seu link exclusivo:</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={fullUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-slate-900 rounded-lg border border-slate-700 text-white"
          />
          <Button onClick={handleCopyLink} variant="outline" className="border-slate-600">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </Button>
        </div>
      </div>

      <div className="flex gap-4 justify-center mb-8">
        <Button 
          onClick={() => window.open(`/pro/${slug}`, '_blank')} 
          className="bg-amber-500 hover:bg-amber-600"
        >
          <Eye size={18} />
          Ver minha página
        </Button>
        <Button 
          onClick={() => {
            const msg = `Confira minha página profissional: ${fullUrl}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
          }}
          variant="outline" 
          className="border-green-500 text-green-400 hover:bg-green-500/10"
        >
          <MessageSquare size={18} />
          Compartilhar
        </Button>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <p className="text-amber-300 text-sm">
          <Star className="inline mr-1" size={14} />
          <strong>Badge Pro ativo!</strong> Sua página mostra o selo de verificado.
        </p>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      default: return renderStep1();
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.name && formData.business;
      case 2: return formData.phone;
      case 3: return formData.template;
      case 4: return formData.services.length > 0;
      case 5: return true;
      case 6: return formData.acceptPix || formData.acceptCard || formData.acceptBoleto;
      case 7: return true;
      default: return true;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/pro')} className="text-slate-400 hover:text-white">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Crown className="text-amber-400" size={20} />
              <span className="font-bold text-white">AtivaJá Pro</span>
            </div>
          </div>
          <span className="text-sm text-slate-400">Passo {step} de 8</span>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-slate-900/50 border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex justify-between">
            {STEPS.map((s) => (
              <button
                key={s.id}
                onClick={() => s.id <= step && setStep(s.id)}
                className={`flex flex-col items-center gap-1 ${
                  s.id <= step ? 'text-amber-400' : 'text-slate-600'
                }`}
                disabled={s.id > step}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  s.id < step
                    ? 'bg-amber-500 text-white'
                    : s.id === step
                      ? 'bg-amber-500/20 border-2 border-amber-500'
                      : 'bg-slate-800 border border-slate-700'
                }`}>
                  {s.id < step ? <Check size={16} /> : <s.icon size={16} />}
                </div>
                <span className="text-xs hidden sm:block">{s.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="py-8 px-4">
        {renderCurrentStep()}
      </main>

      {/* Footer Navigation */}
      {step < 8 && (
        <footer className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-4">
          <div className="max-w-4xl mx-auto flex justify-between">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={step === 1}
              className="border-slate-700 text-slate-300"
            >
              <ArrowLeft size={18} />
              Voltar
            </Button>
            
            {step < 7 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="bg-amber-500 hover:bg-amber-600"
              >
                Próximo
                <ArrowRight size={18} />
              </Button>
            ) : (
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Crown size={18} />}
                Ativar Pro
              </Button>
            )}
          </div>
        </footer>
      )}

      {/* Master Access */}
      <MasterAccess 
        produto="pro" 
        slug={formData.slug}
        onSuccess={() => navigate(`/pro/painel/${formData.slug}`)}
      />
    </div>
  );
};

export default ProConfigurar;
